import { z } from 'zod';
import { PRODUCT_STATUSES } from './catalog';
import { normalizedUuidSchema } from './common';
import { PORTFOLIO_CLIENT_NAME_MAX_LENGTH, PORTFOLIO_TITLE_MAX_LENGTH } from './landing';
import { portfolioSha256Schema, portfolioTimestampSchema } from './portfolio-assets';

export const PORTFOLIO_CATEGORY_NAME_MAX_LENGTH = 30;
export const PORTFOLIO_TEXT_MAX_LENGTH = 2000;
export const PORTFOLIO_MAX_IMAGES = 10;
export const PORTFOLIO_PAGE_SIZE = 12;

const normalizedTextSchema = z.string().transform((value) => value.replace(/\r\n/g, '\n').trim());
const requiredText = (maxLength: number) =>
  normalizedTextSchema.refine(
    (value) => Array.from(value).length >= 1 && Array.from(value).length <= maxLength,
    {
      message: `필수 텍스트는 1~${maxLength}자여야 합니다.`,
    },
  );
const clientNameSchema = normalizedTextSchema
  .refine((value) => Array.from(value).length <= PORTFOLIO_CLIENT_NAME_MAX_LENGTH)
  .transform((value) => value || null)
  .nullable();
const orderSchema = z.number().int().nonnegative();
const opaqueTokenSchema = z.string().min(1);

function rejectDuplicateIds(ids: string[], ctx: z.RefinementCtx, path: string[]) {
  if (new Set(ids).size !== ids.length) {
    ctx.addIssue({ code: 'custom', path, message: '중복된 식별자를 사용할 수 없습니다.' });
  }
}

export const portfolioFieldsSchema = z.strictObject({
  title: requiredText(PORTFOLIO_TITLE_MAX_LENGTH),
  clientName: clientNameSchema,
  companyIntroduction: requiredText(PORTFOLIO_TEXT_MAX_LENGTH),
  productionBackground: requiredText(PORTFOLIO_TEXT_MAX_LENGTH),
});
export type PortfolioFields = z.infer<typeof portfolioFieldsSchema>;

export const portfolioAssetRefSchema = z.strictObject({ assetId: normalizedUuidSchema });
export type PortfolioAssetRef = z.infer<typeof portfolioAssetRefSchema>;
const uploadedImageSchema = z.strictObject({
  kind: z.literal('UPLOAD'),
  assetId: normalizedUuidSchema,
});
export const portfolioImageInputSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('EXISTING'), imageId: normalizedUuidSchema }),
  uploadedImageSchema,
]);
export type PortfolioImageInput = z.infer<typeof portfolioImageInputSchema>;
const keepLogoSchema = z.strictObject({ kind: z.literal('KEEP') });
export const portfolioLogoInputSchema = z.union([z.null(), keepLogoSchema, uploadedImageSchema]);
export type PortfolioLogoInput = z.infer<typeof portfolioLogoInputSchema>;

const saveShape = {
  ...portfolioFieldsSchema.shape,
  images: z.array(portfolioImageInputSchema).min(1).max(PORTFOLIO_MAX_IMAGES),
  categoryIds: z.array(normalizedUuidSchema).min(1),
  productIds: z.array(normalizedUuidSchema),
  logo: portfolioLogoInputSchema,
  isVisible: z.boolean(),
  homeSelected: z.boolean(),
};
function validateSaveReferences(
  value: { images: PortfolioImageInput[]; categoryIds: string[]; productIds: string[] },
  ctx: z.RefinementCtx,
) {
  rejectDuplicateIds(value.categoryIds, ctx, ['categoryIds']);
  rejectDuplicateIds(value.productIds, ctx, ['productIds']);
  // 사진 행 ID와 새 자산 ID는 서로 다른 식별자 공간이다.
  rejectDuplicateIds(
    value.images.map((image) =>
      image.kind === 'EXISTING' ? `existing:${image.imageId}` : `upload:${image.assetId}`,
    ),
    ctx,
    ['images'],
  );
}
export const portfolioSaveInputSchema = z
  .strictObject(saveShape)
  .superRefine(validateSaveReferences);
export type PortfolioSaveInput = z.infer<typeof portfolioSaveInputSchema>;
export const createPortfolioInputSchema = z
  .strictObject({
    ...saveShape,
    id: normalizedUuidSchema,
    createToken: opaqueTokenSchema,
    images: z.array(uploadedImageSchema).min(1).max(PORTFOLIO_MAX_IMAGES),
    logo: z.union([z.null(), uploadedImageSchema]),
  })
  .superRefine(validateSaveReferences);
export type CreatePortfolioInput = z.infer<typeof createPortfolioInputSchema>;
export const updatedAtInputSchema = z.strictObject({
  id: normalizedUuidSchema,
  expectedUpdatedAt: portfolioTimestampSchema,
});
export type UpdatedAtInput = z.infer<typeof updatedAtInputSchema>;
export const updatePortfolioInputSchema = z
  .strictObject({ ...saveShape, ...updatedAtInputSchema.shape })
  .superRefine(validateSaveReferences);
export type UpdatePortfolioInput = z.infer<typeof updatePortfolioInputSchema>;
export const portfolioMutationResultSchema = z.strictObject({
  id: normalizedUuidSchema,
  updatedAt: portfolioTimestampSchema,
});
export type PortfolioMutationResult = z.infer<typeof portfolioMutationResultSchema>;

export const resolvePortfolioCreateInputSchema = z.strictObject({
  id: normalizedUuidSchema,
  createToken: opaqueTokenSchema,
});
export type ResolvePortfolioCreateInput = z.infer<typeof resolvePortfolioCreateInputSchema>;
export const portfolioCreateResolutionSchema = z.strictObject({
  status: z.enum(['SAVED', 'RETRYABLE']),
});
export type PortfolioCreateResolution = z.infer<typeof portfolioCreateResolutionSchema>;

export const portfolioCategorySchema = z.strictObject({
  id: normalizedUuidSchema,
  name: requiredText(PORTFOLIO_CATEGORY_NAME_MAX_LENGTH),
});
export type PortfolioCategory = z.infer<typeof portfolioCategorySchema>;
export const portfolioCardSchema = z.strictObject({
  id: normalizedUuidSchema,
  title: requiredText(PORTFOLIO_TITLE_MAX_LENGTH),
  clientName: clientNameSchema,
  categories: z.array(portfolioCategorySchema).min(1),
  imageUrl: z.url(),
});
export type PortfolioCard = z.infer<typeof portfolioCardSchema>;
export const imageViewSchema = z.strictObject({
  id: normalizedUuidSchema,
  thumbnailUrl: z.url(),
  mediumUrl: z.url(),
  largeUrl: z.url(),
  displayOrder: orderSchema,
});
export type ImageView = z.infer<typeof imageViewSchema>;
const portfolioProductSchema = z.strictObject({
  id: normalizedUuidSchema,
  name: z.string().min(1),
  imageUrl: z.url().nullable(),
});
export const portfolioDetailSchema = z.strictObject({
  ...portfolioCardSchema.shape,
  companyIntroduction: portfolioFieldsSchema.shape.companyIntroduction,
  productionBackground: portfolioFieldsSchema.shape.productionBackground,
  brandLogoUrl: z.url().nullable(),
  images: z.array(imageViewSchema).min(1).max(PORTFOLIO_MAX_IMAGES),
  products: z.array(portfolioProductSchema),
});
export type PortfolioDetail = z.infer<typeof portfolioDetailSchema>;
export const portfolioPageSchema = z.strictObject({
  items: z.array(portfolioCardSchema).max(PORTFOLIO_PAGE_SIZE),
  page: z.number().int().positive(),
  pageSize: z.literal(PORTFOLIO_PAGE_SIZE),
  totalCount: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});
export type PortfolioPage = z.infer<typeof portfolioPageSchema>;

/** HOME "함께한 브랜드" 로고 수 상한 — 데스크톱 여섯 칸 세 줄. */
export const PORTFOLIO_CLIENT_LOGOS_MAX = 18;

/** HOME 함께한 브랜드 — 공개 사례 중 고객사명과 로고가 모두 있는 것만. 배열 순서가 노출 순서다. */
export const portfolioClientLogoSchema = z.strictObject({
  id: normalizedUuidSchema,
  clientName: z.string().min(1),
  brandLogoUrl: z.url(),
});
export type PortfolioClientLogo = z.infer<typeof portfolioClientLogoSchema>;
export const portfolioClientLogoListSchema = z
  .array(portfolioClientLogoSchema)
  .max(PORTFOLIO_CLIENT_LOGOS_MAX);

const adminSettingsShape = {
  isVisible: z.boolean(),
  homeSelected: z.boolean(),
  displayOrder: orderSchema,
  homeDisplayOrder: orderSchema.nullable(),
  createdAt: portfolioTimestampSchema,
  updatedAt: portfolioTimestampSchema,
};
export const portfolioAdminCardSchema = z.strictObject({
  ...portfolioCardSchema.shape,
  ...adminSettingsShape,
});
export type PortfolioAdminCard = z.infer<typeof portfolioAdminCardSchema>;
export const portfolioAdminDetailSchema = z.strictObject({
  ...portfolioDetailSchema.shape,
  ...adminSettingsShape,
  logo: keepLogoSchema.nullable(),
  products: z.array(
    z.strictObject({
      ...portfolioProductSchema.shape,
      status: z.enum(PRODUCT_STATUSES),
      deletedAt: portfolioTimestampSchema.nullable(),
    }),
  ),
});
export type PortfolioAdminDetail = z.infer<typeof portfolioAdminDetailSchema>;
export const portfolioOrderSnapshotSchema = z
  .strictObject({
    orderedIds: z.array(normalizedUuidSchema),
    token: opaqueTokenSchema,
  })
  .superRefine((value, ctx) => rejectDuplicateIds(value.orderedIds, ctx, ['orderedIds']));
export type PortfolioOrderSnapshot = z.infer<typeof portfolioOrderSnapshotSchema>;
export const portfolioReorderInputSchema = z
  .strictObject({
    orderedIds: z.array(normalizedUuidSchema),
    expectedToken: opaqueTokenSchema,
  })
  .superRefine((value, ctx) => rejectDuplicateIds(value.orderedIds, ctx, ['orderedIds']));
export type PortfolioReorderInput = z.infer<typeof portfolioReorderInputSchema>;
export const portfolioAdminListSchema = z.strictObject({
  items: z.array(portfolioAdminCardSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  orderSnapshot: portfolioOrderSnapshotSchema,
  homeOrderSnapshot: portfolioOrderSnapshotSchema,
});
export type PortfolioAdminList = z.infer<typeof portfolioAdminListSchema>;
export const portfolioCategoryAdminSchema = z.strictObject({
  ...portfolioCategorySchema.shape,
  displayOrder: orderSchema,
  linkedPortfolioCount: z.number().int().nonnegative(),
  createdAt: portfolioTimestampSchema,
  updatedAt: portfolioTimestampSchema,
});
export type PortfolioCategoryAdmin = z.infer<typeof portfolioCategoryAdminSchema>;
export const portfolioCategoryAdminListSchema = z.strictObject({
  items: z.array(portfolioCategoryAdminSchema),
  orderSnapshot: portfolioOrderSnapshotSchema,
});
export type PortfolioCategoryAdminList = z.infer<typeof portfolioCategoryAdminListSchema>;

/** 서버 내부 등록 의도. 고객 응답에 포함하지 않는다. */
export const portfolioCreateIntentSchema = z.strictObject({
  createId: normalizedUuidSchema,
  createToken: opaqueTokenSchema,
  adminId: normalizedUuidSchema,
  payloadHash: portfolioSha256Schema.nullable(),
  state: z.enum(['ISSUED', 'COMPLETED', 'DELETED']),
  expiresAt: portfolioTimestampSchema,
});
export type PortfolioCreateIntent = z.infer<typeof portfolioCreateIntentSchema>;
